import React from 'react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toFixed(4);
  }
  return String(v);
}

function fmtPct(v) {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(2)}%`;
}

function fmtUsd(v) {
  if (v === null || v === undefined) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
}

function DirectionBadge({ value }) {
  const v = (value || '').toLowerCase();
  if (v === 'up' || v === 'bullish' || v === 'into_this_sector')
    return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-green-900/50 border border-green-700/50 text-green-300">▲ {value}</span>;
  if (v === 'down' || v === 'bearish' || v === 'out_of_sector')
    return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-red-900/50 border border-red-700/50 text-red-300">▼ {value}</span>;
  return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-slate-700/60 border border-slate-600 text-slate-400">{value || '—'}</span>;
}

function ScoreBar({ value, max = 1 }) {
  if (value === null || value === undefined) return null;
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color = pct >= 70 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-orange-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-400 w-10 text-right shrink-0">{value.toFixed(2)}</span>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2 py-1 border-b border-slate-700/40 last:border-0">
      <span className="text-xs text-slate-500 w-44 shrink-0 pt-0.5">{label}</span>
      <span className="text-xs text-slate-200 flex-1">{children}</span>
    </div>
  );
}

// ── Bucket-specific renderers ─────────────────────────────────────────────────

function MomentumView({ data }) {
  return (
    <div className="space-y-0.5">
      <Row label="Direction"><DirectionBadge value={data.direction} /></Row>
      {data.score !== undefined && <Row label="Score"><ScoreBar value={data.score} /></Row>}
      {data.roc_primary !== undefined && <Row label="ROC (primary)">{fmtPct(data.roc_primary / 100)}</Row>}
      {data.roc_short !== undefined && <Row label="ROC (short)">{fmtPct(data.roc_short / 100)}</Row>}
      {data.roc_long !== undefined && <Row label="ROC (long)">{fmtPct(data.roc_long / 100)}</Row>}
      {data.composite_score !== undefined && <Row label="Composite score">{data.composite_score.toFixed(3)}</Row>}
    </div>
  );
}

function VolumeBreakoutView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.breakout !== undefined && (
        <Row label="Breakout">
          {data.breakout
            ? <span className="text-green-400 font-semibold">Yes</span>
            : <span className="text-slate-400">No</span>}
        </Row>
      )}
      {data.score !== undefined && <Row label="Score"><ScoreBar value={data.score} /></Row>}
      {data.volume_ratio !== undefined && <Row label="Volume ratio">{data.volume_ratio.toFixed(2)}×</Row>}
      {data.current_volume !== undefined && <Row label="Current vol">{fmt(data.current_volume)}</Row>}
      {data.avg_volume !== undefined && <Row label="Avg vol">{fmt(data.avg_volume)}</Row>}
      {data.price_direction !== undefined && <Row label="Price direction"><DirectionBadge value={data.price_direction} /></Row>}
      {data.is_wash_trading_signal && (
        <Row label="Warning">
          <span className="text-amber-400">⚠ Possible wash trading (high vol, flat price)</span>
        </Row>
      )}
    </div>
  );
}

function TrendingSocialView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.sentiment_direction !== undefined && <Row label="Sentiment"><DirectionBadge value={data.sentiment_direction} /></Row>}
      {data.sentiment_score !== undefined && (
        <Row label="Sentiment score">
          <ScoreBar value={(data.sentiment_score + 1) / 2} />
        </Row>
      )}
      {data.mention_count !== undefined && <Row label="Mentions">{data.mention_count} articles</Row>}
      {data.engagement_score !== undefined && <Row label="Engagement"><ScoreBar value={data.engagement_score} /></Row>}
      {data.tier_breakdown && (
        <Row label="Source tiers">
          T1: {data.tier_breakdown.tier_1 ?? 0} · T2: {data.tier_breakdown.tier_2 ?? 0} · T3: {data.tier_breakdown.tier_3 ?? 0}
        </Row>
      )}
      {data.notable_headlines?.length > 0 && (
        <div className="pt-1">
          <p className="text-xs text-slate-500 mb-1">Notable headlines</p>
          <ul className="space-y-1">
            {data.notable_headlines.slice(0, 3).map((h, i) => (
              <li key={i} className="text-xs text-slate-300 leading-snug pl-2 border-l-2 border-cyan-700/50">{h}</li>
            ))}
          </ul>
        </div>
      )}
      {data.articles?.length > 0 && (
        <div className="pt-1">
          <p className="text-xs text-slate-500 mb-1">Articles</p>
          <ul className="space-y-1">
            {data.articles.slice(0, 3).map((a, i) => (
              <li key={i} className="text-xs">
                <a href={a.url} target="_blank" rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 underline leading-snug">
                  {a.title}
                </a>
                {a.timeframe_relevance && (
                  <span className="ml-1 text-slate-500">({a.timeframe_relevance})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function WhaleActivityView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.bias !== undefined && <Row label="Bias"><DirectionBadge value={data.bias} /></Row>}
      {data.net_whale_flow_usd !== undefined && (
        <Row label="Net whale flow">
          <span className={data.net_whale_flow_usd >= 0 ? 'text-green-400' : 'text-red-400'}>
            {fmtUsd(data.net_whale_flow_usd)}
          </span>
        </Row>
      )}
      {data.long_volume_usd !== undefined && <Row label="Long vol">{fmtUsd(data.long_volume_usd)}</Row>}
      {data.short_volume_usd !== undefined && <Row label="Short vol">{fmtUsd(data.short_volume_usd)}</Row>}
      {data.whale_count !== undefined && <Row label="Whale wallets">{data.whale_count}</Row>}
      {data.tracked_wallet_count !== undefined && <Row label="Tracked wallets">{data.tracked_wallet_count}</Row>}
      {data.events?.length > 0 && (
        <div className="pt-1">
          <p className="text-xs text-slate-500 mb-1">Recent events</p>
          <div className="space-y-1">
            {data.events.slice(0, 4).map((ev, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <DirectionBadge value={ev.direction} />
                <span className="text-slate-300">{(ev.event_type || '').replace(/_/g, ' ')}</span>
                <span className="text-slate-400">{fmtUsd(ev.size_usd)}</span>
                {ev.leverage && <span className="text-slate-500">{ev.leverage}×</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FundingRateView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.interpretation !== undefined && <Row label="Signal"><DirectionBadge value={data.interpretation} /></Row>}
      {data.current_rate !== undefined && <Row label="Current rate">{fmtPct(data.current_rate)}</Row>}
      {data.avg_rate !== undefined && <Row label="Avg rate">{fmtPct(data.avg_rate)}</Row>}
      {data.deviation_from_average !== undefined && (
        <Row label="Deviation">
          <span className={data.deviation_from_average > 0 ? 'text-amber-400' : 'text-cyan-400'}>
            {data.deviation_from_average > 0 ? '+' : ''}{fmtPct(data.deviation_from_average)}
          </span>
        </Row>
      )}
      {data.extreme_signal !== undefined && (
        <Row label="Extreme signal">
          {data.extreme_signal
            ? <span className="text-amber-400">⚠ Yes</span>
            : <span className="text-slate-400">No</span>}
        </Row>
      )}
    </div>
  );
}

function SectorRotationView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.sector !== undefined && <Row label="Sector"><span className="text-cyan-300">{data.sector}</span></Row>}
      {data.sector_rotation_direction !== undefined && <Row label="Rotation"><DirectionBadge value={data.sector_rotation_direction} /></Row>}
      {data.token_momentum_score !== undefined && <Row label="Token momentum"><ScoreBar value={data.token_momentum_score} /></Row>}
      {data.sector_momentum && (
        <div className="pt-1">
          <p className="text-xs text-slate-500 mb-1">Sector momentum</p>
          <div className="space-y-1">
            {Object.entries(data.sector_momentum).slice(0, 5).map(([sec, v]) => (
              <div key={sec} className="flex items-center gap-2 text-xs">
                <span className="text-slate-400 w-24 shrink-0 truncate">{sec}</span>
                <ScoreBar value={Math.max(0, Math.min(1, (v.avg_momentum || 0 + 1) / 2))} />
                <span className="text-slate-500 shrink-0">{v.token_count} tokens</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CorrelationDivergenceView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.move_type !== undefined && <Row label="Move type"><span className="text-cyan-300">{data.move_type}</span></Row>}
      {data.btc_correlation !== undefined && (
        <Row label="BTC correlation">
          <ScoreBar value={(data.btc_correlation + 1) / 2} />
        </Row>
      )}
      {data.eth_correlation !== undefined && (
        <Row label="ETH correlation">
          <ScoreBar value={(data.eth_correlation + 1) / 2} />
        </Row>
      )}
      {data.btc_diverging !== undefined && (
        <Row label="BTC diverging">
          {data.btc_diverging ? <span className="text-cyan-400">Yes</span> : <span className="text-slate-400">No</span>}
        </Row>
      )}
      {data.eth_diverging !== undefined && (
        <Row label="ETH diverging">
          {data.eth_diverging ? <span className="text-cyan-400">Yes</span> : <span className="text-slate-400">No</span>}
        </Row>
      )}
      {data.divergence_magnitude !== undefined && <Row label="Divergence magnitude">{data.divergence_magnitude.toFixed(3)}</Row>}
    </div>
  );
}

function LiquidationClustersView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.momentum_alignment !== undefined && <Row label="Alignment"><span className={data.momentum_alignment === 'aligned' ? 'text-green-400' : 'text-amber-400'}>{data.momentum_alignment}</span></Row>}
      {data.oi_concentration !== undefined && <Row label="OI concentration"><ScoreBar value={data.oi_concentration} /></Row>}
      {data.open_interest !== undefined && <Row label="Open interest">{fmtUsd(data.open_interest)}</Row>}
      {data.current_price !== undefined && <Row label="Current price">${data.current_price.toLocaleString()}</Row>}
      {data.liquidation_level_long !== undefined && <Row label="Liq level (longs)">${data.liquidation_level_long.toLocaleString()}</Row>}
      {data.liquidation_level_short !== undefined && <Row label="Liq level (shorts)">${data.liquidation_level_short.toLocaleString()}</Row>}
      {data.distance_to_liquidation_pct !== undefined && (
        <Row label="Distance to liq">{fmtPct(data.distance_to_liquidation_pct / 100)}</Row>
      )}
    </div>
  );
}

function NewListingsView({ data }) {
  return (
    <div className="space-y-0.5">
      {data.early_volume_trend !== undefined && <Row label="Volume trend"><DirectionBadge value={data.early_volume_trend} /></Row>}
      {data.days_since_listing !== undefined && <Row label="Days since listing">{data.days_since_listing}</Row>}
      {data.max_leverage !== undefined && <Row label="Max leverage">{data.max_leverage}×</Row>}
      {data.market_cap !== undefined && <Row label="Market cap">{fmtUsd(data.market_cap)}</Row>}
      {data.market_cap_rank !== undefined && <Row label="Market cap rank">#{data.market_cap_rank}</Row>}
      {data.categories?.length > 0 && (
        <Row label="Categories">
          <span className="flex flex-wrap gap-1">
            {data.categories.slice(0, 5).map((c) => (
              <span key={c} className="px-1.5 py-0.5 rounded text-xs bg-slate-700/60 border border-slate-600 text-slate-300">{c}</span>
            ))}
          </span>
        </Row>
      )}
      {data.kraken_listing_activity_summary !== undefined && (
        <Row label="Activity">{data.kraken_listing_activity_summary}</Row>
      )}
      {data.coingecko_categories && (
        <>
          {data.coingecko_categories.market_cap !== undefined && (
            <Row label="Market cap">{fmtUsd(data.coingecko_categories.market_cap)}</Row>
          )}
          {data.coingecko_categories.market_cap_rank !== undefined && (
            <Row label="Market cap rank">#{data.coingecko_categories.market_cap_rank}</Row>
          )}
          {data.coingecko_categories.categories?.length > 0 && (
            <Row label="Categories">
              <span className="flex flex-wrap gap-1">
                {data.coingecko_categories.categories.slice(0, 5).map((c) => (
                  <span key={c} className="px-1.5 py-0.5 rounded text-xs bg-slate-700/60 border border-slate-600 text-slate-300">{c}</span>
                ))}
              </span>
            </Row>
          )}
        </>
      )}
    </div>
  );
}

// Generic fallback: render top-level scalar fields as key-value rows
function GenericView({ data }) {
  const scalars = Object.entries(data).filter(([, v]) => typeof v !== 'object' || v === null);
  return (
    <div className="space-y-0.5">
      {scalars.map(([k, v]) => (
        <Row key={k} label={k.replace(/_/g, ' ')}>
          {typeof v === 'boolean'
            ? (v ? <span className="text-green-400">Yes</span> : <span className="text-slate-400">No</span>)
            : fmt(v)}
        </Row>
      ))}
    </div>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

const RENDERERS = {
  MOMENTUM: MomentumView,
  VOLUME_BREAKOUT: VolumeBreakoutView,
  TRENDING_SOCIAL: TrendingSocialView,
  WHALE_ACTIVITY: WhaleActivityView,
  FUNDING_RATE_ANOMALY: FundingRateView,
  SECTOR_ROTATION: SectorRotationView,
  CORRELATION_DIVERGENCE: CorrelationDivergenceView,
  LIQUIDATION_CLUSTERS: LiquidationClustersView,
  NEW_LISTINGS: NewListingsView,
};

/**
 * Renders bucket_data in a human-readable format.
 * Props:
 *   bucket    — string bucket key (e.g. "MOMENTUM")
 *   data      — the bucket_data object
 */
export default function BucketDataView({ bucket, data }) {
  if (!data || Object.keys(data).length === 0) return null;

  const Renderer = RENDERERS[bucket] || GenericView;

  return (
    <div className="mt-3 p-3 bg-slate-900/70 rounded-lg border border-slate-700/50">
      <Renderer data={data} />
    </div>
  );
}
